import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Music, Download, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentInfo(data);
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling if not yet paid (max 5 attempts)
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('pending');
          setPaymentInfo(data);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 3) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-cream pt-32 pb-20" data-testid="payment-success-page">
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 text-gold-500 animate-spin mx-auto mb-6" />
                <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                  {t('Processing Payment...', 'Procesando Pago...')}
                </h1>
                <p className="font-lato text-gray-600">
                  {t('Please wait while we confirm your payment.', 'Por favor espere mientras confirmamos su pago.')}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                  {t('Payment Successful!', '¡Pago Exitoso!')}
                </h1>
                <p className="font-lato text-gray-600 mb-6">
                  {t(
                    'Thank you for supporting Sarah Capeles\' ministry. Your purchase helps spread the Gospel through music.',
                    'Gracias por apoyar el ministerio de Sarah Capeles. Tu compra ayuda a difundir el Evangelio a través de la música.'
                  )}
                </p>
                
                {paymentInfo && (
                  <div className="bg-cream rounded-xl p-6 mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      {paymentInfo.image_url ? (
                        <img
                          src={paymentInfo.image_url}
                          alt={paymentInfo.album_title}
                          className="w-16 h-16 rounded object-cover"
                        />
                      ) : (
                        <Music className="w-8 h-8 text-gold-500" />
                      )}
                      <span className="font-playfair text-xl font-semibold text-charcoal">
                        {paymentInfo.album_title}
                      </span>
                    </div>
                    <p className="font-lato text-gray-600 text-sm">
                      {t('Amount paid:', 'Monto pagado:')} ${(paymentInfo.amount_total || 0).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </p>
                  </div>
                )}

                {paymentInfo?.download_url && (
                  <div className="mb-8" data-testid="success-download-block">
                    <a
                      href={paymentInfo.download_url}
                      className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-white font-lato uppercase tracking-wider px-8 py-4 rounded text-base shadow-lg transition-colors"
                      data-testid="success-download-button"
                    >
                      <Download className="w-5 h-5" />
                      {t('Download Your Music', 'Descarga Tu Música')}
                    </a>
                    <p className="font-lato text-xs text-gray-500 mt-3">
                      {t(
                        'A copy of this link has also been emailed to you.',
                        'También te enviamos una copia de este enlace por correo.'
                      )}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <p className="font-lato text-sm text-gray-500">
                    {t(
                      'A confirmation email will be sent to you shortly. If you have any questions, please contact us.',
                      'Se le enviará un correo de confirmación en breve. Si tiene alguna pregunta, por favor contáctenos.'
                    )}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                    <Link to="/music">
                      <Button variant="outline" className="border-gold-500 text-gold-600 w-full sm:w-auto">
                        <Music className="w-4 h-4 mr-2" />
                        {t('Browse More Music', 'Ver Más Música')}
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button className="bg-gold-500 hover:bg-gold-600 text-white w-full sm:w-auto">
                        {t('Back to Home', 'Volver al Inicio')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </>
            )}

            {status === 'pending' && (
              <>
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-12 h-12 text-yellow-500" />
                </div>
                <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                  {t('Payment Processing', 'Procesando Pago')}
                </h1>
                <p className="font-lato text-gray-600 mb-6">
                  {t(
                    'Your payment is being processed. This may take a few moments. Please check your email for confirmation.',
                    'Su pago está siendo procesado. Esto puede tomar unos momentos. Por favor revise su correo para confirmación.'
                  )}
                </p>
                <Link to="/">
                  <Button className="bg-gold-500 hover:bg-gold-600 text-white">
                    {t('Back to Home', 'Volver al Inicio')}
                  </Button>
                </Link>
              </>
            )}

            {status === 'expired' && (
              <>
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-gray-500" />
                </div>
                <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                  {t('Session Expired', 'Sesión Expirada')}
                </h1>
                <p className="font-lato text-gray-600 mb-6">
                  {t(
                    'Your payment session has expired. Please try again.',
                    'Su sesión de pago ha expirado. Por favor intente de nuevo.'
                  )}
                </p>
                <Link to="/music">
                  <Button className="bg-gold-500 hover:bg-gold-600 text-white">
                    {t('Try Again', 'Intentar de Nuevo')}
                  </Button>
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h1 className="font-playfair text-3xl font-bold text-charcoal mb-4">
                  {t('Something Went Wrong', 'Algo Salió Mal')}
                </h1>
                <p className="font-lato text-gray-600 mb-6">
                  {t(
                    'We couldn\'t verify your payment. If you were charged, please contact us with your payment details.',
                    'No pudimos verificar su pago. Si se le cobró, por favor contáctenos con los detalles de su pago.'
                  )}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button variant="outline" className="border-gold-500 text-gold-600 w-full sm:w-auto">
                      {t('Contact Us', 'Contáctenos')}
                    </Button>
                  </Link>
                  <Link to="/music">
                    <Button className="bg-gold-500 hover:bg-gold-600 text-white w-full sm:w-auto">
                      {t('Try Again', 'Intentar de Nuevo')}
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Scripture */}
          <div className="text-center mt-12">
            <blockquote className="font-playfair text-xl text-charcoal italic">
              {t(
                '"Give thanks to the Lord, for he is good; his love endures forever."',
                '"Dad gracias al Señor, porque él es bueno; su amor es eterno."'
              )}
            </blockquote>
            <p className="font-lato text-gold-600 text-sm uppercase tracking-wider mt-2">
              — {t('Psalm 107:1', 'Salmo 107:1')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
